"use client";
import { motion } from "framer-motion";
import { RotateCcw, ArrowLeft, CreditCard, Clock, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto px-4 py-8"
      >
        <div className="flex items-center justify-between mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>

        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6"
          >
            <RotateCcw className="h-8 w-8 text-primary" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-foreground mb-4"
          >
            Cancellation & Refund Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-muted-foreground text-lg max-w-2xl mx-auto"
          >
            Our policies regarding cancellations, refunds, and credits
          </motion.p>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="max-w-4xl mx-auto"
        >
          <div className="p-8 rounded-2xl bg-card/50 backdrop-blur-sm border border-white/[0.1] space-y-8">

            {/* Overview */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Service Overview</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  AssignOwl is a <strong className="text-foreground">paid service</strong> that uses a credit-based system. Credits are purchased to access our AI-powered assignment grading and evaluation features.
                </p>
                <p>
                  <strong className="text-foreground">Current Pricing:</strong>
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>$5 for 40 credits (perfect for trying once)</li>
                  <li>$20 for 260 credits (best value - save 60%)</li>
                  <li>20 credits = 1 assignment grading</li>
                </ul>
              </div>
            </section>

            {/* Credit-Based Service */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Credit-Based System</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Our service operates on a pay-per-use credit model:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Purchase credits to grade assignments</li>
                  <li>20 credits = 1 complete assignment evaluation</li>
                  <li>Credits never expire once purchased</li>
                  <li>No subscription - pay only for what you use</li>
                  <li>Credits can be used for assignment grading and future services</li>
                </ul>
              </div>
            </section>

            {/* Refund Eligibility */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Refund Eligibility</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>
                  <strong className="text-foreground">Unused Credits:</strong> You may request a refund for unused credits within 7 days of purchase if you have not used any of the credits purchased in that transaction.
                </p>
                <p>
                  <strong className="text-foreground">Technical Issues:</strong> If our service experiences significant technical issues that prevent you from using your credits, you may be eligible for a refund or replacement credits.
                </p>
                <p>
                  <strong className="text-foreground">Payment Errors:</strong> If you are charged incorrectly or experience duplicate charges, we will promptly issue a full refund for the erroneous charge.
                </p>
                <p>
                  <strong className="text-foreground">Service Failure:</strong> If our grading service fails to deliver results after consuming your credit, we will either refund the credit or provide a replacement credit.
                </p>
              </div>
            </section>

            {/* Non-Refundable */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Ban className="h-5 w-5 text-red-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground">Non-Refundable Situations</h2>
              </div>
              <div className="space-y-4 text-muted-foreground ml-13">
                <p>Refunds will NOT be provided for:</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li><strong className="text-foreground">Used Credits:</strong> Credits that have been used to grade assignments cannot be refunded</li>
                  <li><strong className="text-foreground">After 7 Days:</strong> Refund requests made more than 7 days after purchase (for unused credits)</li>
                  <li><strong className="text-foreground">Policy Violations:</strong> Account terminations due to Terms of Service violations</li>
                  <li><strong className="text-foreground">Change of Mind:</strong> Simply changing your mind after using credits</li>
                  <li><strong className="text-foreground">Dissatisfaction with Results:</strong> Disagreement with grading results (our AI provides analysis, not guarantees)</li>
                  <li><strong className="text-foreground">User Error:</strong> Uploading wrong files or misusing the service</li>
                </ul>
              </div>
            </section>

            {/* Subscription Cancellation */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">No Subscriptions</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Pay-Per-Use Model:</strong> AssignOwl does not use subscriptions. You simply purchase credits as needed.
                </p>
                <p>
                  <strong className="text-foreground">No Auto-Renewals:</strong> Credits do not auto-renew. You only pay when you choose to purchase more credits.
                </p>
                <p>
                  <strong className="text-foreground">Credits Never Expire:</strong> Your purchased credits remain in your account indefinitely and can be used at any time.
                </p>
                <p>
                  <strong className="text-foreground">Account Closure:</strong> You may close your account at any time. Unused credits in your account will be forfeited upon account closure.
                </p>
              </div>
            </section>

            {/* Refund Process */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">How to Request a Refund</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  To request a refund for unused credits:
                </p>
                <ol className="space-y-2 list-decimal list-inside">
                  <li>Contact our support team at <strong className="text-foreground">support@assignowl.com</strong></li>
                  <li>Include your account email and transaction ID</li>
                  <li>Specify which purchase you want refunded (must be within 7 days and credits unused)</li>
                  <li>Provide a brief explanation for your refund request</li>
                  <li>Allow 5-7 business days for review and processing</li>
                </ol>
                <p className="mt-4">
                  Approved refunds will be issued to the original payment method within 10-14 business days.
                </p>
              </div>
            </section>

            {/* Credits and Alternatives */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Service Credits & Compensation</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  In lieu of refunds, we may offer additional service credits for:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Extended service outages or technical difficulties</li>
                  <li>Failed grading attempts due to system errors</li>
                  <li>Goodwill gestures for service issues</li>
                  <li>Compensation for delayed or incomplete results</li>
                </ul>
                <p className="mt-4">
                  Compensation credits are added directly to your account and never expire.
                </p>
              </div>
            </section>

            {/* Account Deletion */}
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">Account Deletion</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  You may delete your AssignOwl account at any time:
                </p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>Navigate to your account settings</li>
                  <li>Select "Delete Account"</li>
                  <li>Confirm deletion (this action is irreversible)</li>
                  <li>Your data will be permanently removed within 30 days</li>
                </ul>
                <p className="text-yellow-600 dark:text-yellow-500 font-medium mt-4">
                  Note: Account deletion is permanent and cannot be undone. Please export any important data before deleting your account.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="pt-6 border-t border-white/[0.1]">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Questions?</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>If you have questions about our cancellation and refund policy, please contact us:</p>
                <p className="text-foreground">
                  <strong>Email:</strong> support@assignowl.com<br />
                  <strong>Company:</strong> Greenguide Solutions Pvt Limited
                </p>
              </div>
            </section>

            <div className="text-sm text-muted-foreground text-center pt-6 border-t border-white/[0.1]">
              Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
